import {
    type InlineDataTableColumn,
    type InlineDataTableWidgetProps,
} from '@fruitful/types';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import type {
    LayoutChangeEvent,
    NativeScrollEvent,
    NativeSyntheticEvent,
} from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    GRADIENT_COLOR_BOTTOM,
    GRADIENT_COLOR_TOP,
    LabelEmphasized,
    ScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    formatInlineDataTableCell,
    withAutoTotalsRow,
} from './inlineDataTableUtils';

const ROW_BORDER_COLOR = 'rgba(0, 0, 0, 0.16)';
const COLUMN_GAP = 16;
const ROW_PADDING_Y = 4;
const TEXT_CELL_MAX_LINES = 3;
const SCROLL_EDGE_THRESHOLD = 2;
const SCROLL_FADE_WIDTH = 44;

/** Midpoint of two `#rrggbb` colors, returned as `#rrggbb`. */
const blendHexColors = (from: string, to: string): string => {
    const channels = [1, 3, 5].map((offset) =>
        Math.round(
            (parseInt(from.slice(offset, offset + 2), 16) +
                parseInt(to.slice(offset, offset + 2), 16)) /
                2,
        )
            .toString(16)
            .padStart(2, '0'),
    );

    return `#${channels.join('')}`;
};

const SCROLL_FADE_COLOR = blendHexColors(
    GRADIENT_COLOR_TOP,
    GRADIENT_COLOR_BOTTOM,
);
const SCROLL_FADE_TRANSPARENT = `${SCROLL_FADE_COLOR}00`;

const getTextAlign = (column: InlineDataTableColumn): 'left' | 'right' => {
    if (column.align != null) {
        return column.align === 'left' ? 'left' : 'right';
    }
    return column.format === 'currency' || column.format === 'percent'
        ? 'right'
        : 'left';
};

export const InlineDataTableWidget = ({
    columns,
    rows,
    accountColumnHeader = 'Account',
    totalsRowLabel,
}: InlineDataTableWidgetProps) => {
    const [containerWidth, setContainerWidth] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [measuredWidths, setMeasuredWidths] = useState<Record<
        string,
        number
    > | null>(null);

    const pendingWidths = useRef<Record<string, number>>({});
    const expectedCount = columns.length;

    const displayRows = useMemo(
        () => withAutoTotalsRow(rows, columns, totalsRowLabel),
        [columns, rows, totalsRowLabel],
    );

    const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
        const nextWidth = event.nativeEvent.layout.width;
        setContainerWidth((prev) =>
            Math.abs(prev - nextWidth) < 1 ? prev : nextWidth,
        );
    }, []);

    const handleColumnLayout = useCallback(
        (key: string, event: LayoutChangeEvent) => {
            const width = event.nativeEvent.layout.width;
            pendingWidths.current[key] = width;
            if (Object.keys(pendingWidths.current).length >= expectedCount) {
                setMeasuredWidths({ ...pendingWidths.current });
            }
        },
        [expectedCount],
    );

    const handleHorizontalScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            const { contentOffset, contentSize, layoutMeasurement } =
                event.nativeEvent;
            const maxOffset = Math.max(
                0,
                contentSize.width - layoutMeasurement.width,
            );
            setCanScrollLeft(contentOffset.x > SCROLL_EDGE_THRESHOLD);
            setCanScrollRight(
                contentOffset.x < maxOffset - SCROLL_EDGE_THRESHOLD,
            );
        },
        [],
    );

    // Invisible measurement pass: column-major layout to get natural widths
    const measureColumns = (
        <View
            position="absolute"
            opacity={0}
            pointerEvents="none"
            accessibilityElementsHidden
        >
            <XStack gap={COLUMN_GAP}>
                {columns.map((column) => (
                    <YStack
                        key={column.key}
                        onLayout={(e: LayoutChangeEvent) => {
                            handleColumnLayout(column.key, e);
                        }}
                    >
                        <LabelEmphasized>{column.header}</LabelEmphasized>
                        {displayRows.map((row, i) => {
                            const isTotals = row.isTotalsRow === true;
                            const ValueText = isTotals
                                ? BodyMediumEmphasized
                                : BodyMediumRegular;
                            const formatted = formatInlineDataTableCell(
                                row.cells[column.key] ?? '',
                                column.format,
                            );
                            const displayValue =
                                formatted === ''
                                    ? isTotals
                                        ? ''
                                        : '—'
                                    : formatted;
                            return (
                                <ValueText
                                    key={`measure-${column.key}-${i.toString()}`}
                                >
                                    {displayValue}
                                </ValueText>
                            );
                        })}
                    </YStack>
                ))}
            </XStack>
        </View>
    );

    // Visible row-major table using measured widths
    const table = measuredWidths != null && (
        <YStack width="100%">
            {/* Header row */}
            <XStack
                width="100%"
                alignItems="flex-start"
                gap={COLUMN_GAP}
                paddingVertical={ROW_PADDING_Y}
                borderBottomWidth={1}
                borderBottomColor={ROW_BORDER_COLOR}
            >
                <YStack flex={1} flexShrink={1}>
                    <LabelEmphasized color="$black">
                        {accountColumnHeader}
                    </LabelEmphasized>
                </YStack>
                {columns.map((column) => (
                    <YStack
                        key={column.key}
                        width={measuredWidths[column.key]}
                        flexShrink={0}
                    >
                        <LabelEmphasized
                            color="$black"
                            textAlign={getTextAlign(column)}
                        >
                            {column.header}
                        </LabelEmphasized>
                    </YStack>
                ))}
            </XStack>

            {/* Data rows */}
            {displayRows.map((row, i) => {
                const isTotals = row.isTotalsRow === true;
                const LabelText = isTotals
                    ? BodyMediumEmphasized
                    : BodyMediumRegular;
                const ValueText = isTotals
                    ? BodyMediumEmphasized
                    : BodyMediumRegular;
                return (
                    <XStack
                        key={`${row.label}-${i.toString()}`}
                        width="100%"
                        alignItems="flex-start"
                        gap={COLUMN_GAP}
                        paddingVertical={ROW_PADDING_Y}
                        borderBottomWidth={isTotals ? 0 : 1}
                        borderBottomColor={ROW_BORDER_COLOR}
                    >
                        <YStack flex={1} flexShrink={1}>
                            <LabelText
                                color="$black"
                                numberOfLines={TEXT_CELL_MAX_LINES}
                            >
                                {row.label}
                            </LabelText>
                        </YStack>
                        {columns.map((column) => {
                            const formatted = formatInlineDataTableCell(
                                row.cells[column.key] ?? '',
                                column.format,
                            );
                            const displayValue =
                                formatted === ''
                                    ? isTotals
                                        ? ''
                                        : '—'
                                    : formatted;
                            return (
                                <YStack
                                    key={`${row.label}-${column.key}`}
                                    width={measuredWidths[column.key]}
                                    flexShrink={0}
                                >
                                    <ValueText
                                        color="$black"
                                        textAlign={getTextAlign(column)}
                                    >
                                        {displayValue}
                                    </ValueText>
                                </YStack>
                            );
                        })}
                    </XStack>
                );
            })}
        </YStack>
    );

    const totalValueColumnsWidth =
        measuredWidths != null
            ? columns.reduce(
                  (sum, col) => sum + (measuredWidths[col.key] ?? 0),
                  0,
              ) +
              columns.length * COLUMN_GAP
            : 0;
    const fitsContainer =
        containerWidth <= 0 ||
        measuredWidths == null ||
        totalValueColumnsWidth < containerWidth * 0.85;

    return (
        <YStack width="100%" onLayout={handleContainerLayout}>
            {measureColumns}
            {fitsContainer ? (
                table
            ) : (
                <YStack width="100%" position="relative">
                    <ScrollView
                        horizontal
                        width="100%"
                        showsHorizontalScrollIndicator
                        scrollEventThrottle={16}
                        onScroll={handleHorizontalScroll}
                        accessibilityHint="Swipe horizontally to see more columns"
                        contentContainerStyle={{
                            minWidth: '100%',
                            flexGrow: 1,
                        }}
                    >
                        {table}
                    </ScrollView>
                    {containerWidth > 0 && canScrollLeft && (
                        <TableScrollAffordance side="left" />
                    )}
                    {containerWidth > 0 && canScrollRight && (
                        <TableScrollAffordance side="right" />
                    )}
                </YStack>
            )}
        </YStack>
    );
};

const TableScrollAffordance = ({ side }: { side: 'left' | 'right' }) => {
    const isLeft = side === 'left';

    return (
        <LinearGradient
            colors={
                isLeft
                    ? [SCROLL_FADE_COLOR, SCROLL_FADE_TRANSPARENT]
                    : [SCROLL_FADE_TRANSPARENT, SCROLL_FADE_COLOR]
            }
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            pointerEvents="none"
            style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                [side]: 0,
                width: SCROLL_FADE_WIDTH,
            }}
        />
    );
};
